/**
 * Inspection Mode Component - Phase 4 Updated
 * Features:
 * - Manual inspection window date input
 * - Facility Walkthrough with actual checklist
 * - Missing documents shown with actual names (X notation)
 * - Staff/Child ratios aligned with dashboard input
 * - Real data from compliance API for facility/emergency items
 * - Staff/Children comparison components
 */
import { useState, useEffect, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogDescription, DialogFooter
} from "@/components/ui/dialog";
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger
} from "@/components/ui/tooltip";
import { toast } from "sonner";
import axios from "axios";
import {
  AlertTriangle, Shield, CheckCircle2, XCircle,
  FileText, Users, Printer, Mail, Calendar,
  ClipboardCheck, Building, AlertCircle, Moon,
  Upload, Paperclip, ChevronDown, ChevronUp, Loader2, Eye,
  FolderOpen, Plus, Trash2, Download, Send
} from "lucide-react";
import { Textarea } from "@/components/ui/textarea";

// Import comparison components
import StaffComparisonCard from "./StaffComparisonCard";
import ChildrenComparisonCard from "./ChildrenComparisonCard";
import FeatureGate, { LockedOverlay } from "@/components/FeatureGate";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// State-mandated ratios (same as dashboard)
const RATIOS = {
  infant: { label: 'Infants (0-12mo)', ratio: 4, napRatio: 8 },
  youngToddler: { label: 'Young Toddlers (12-24mo)', ratio: 5, napRatio: 10 },
  olderToddler: { label: 'Older Toddlers (24-36mo)', ratio: 6, napRatio: 12 },
  preschool: { label: 'Preschool (3-5yr)', ratio: 10, napRatio: 20 },
  schoolAge: { label: 'School Age (5+)', ratio: 15, napRatio: 30 }
};

// Facility Walkthrough Checklist Items
const WALKTHROUGH_CHECKLIST = [
  { id: 'exits_clear', label: 'Emergency exits clear and accessible', category: 'Safety' },
  { id: 'fire_extinguishers', label: 'Fire extinguishers accessible and inspected', category: 'Safety' },
  { id: 'first_aid', label: 'First aid kit stocked and accessible', category: 'Safety' },
  { id: 'smoke_detectors', label: 'Smoke detectors working', category: 'Safety' },
  { id: 'emergency_numbers', label: 'Emergency numbers posted', category: 'Safety' },
  { id: 'evacuation_plan', label: 'Evacuation plan posted', category: 'Safety' },
  { id: 'outdoor_safe', label: 'Outdoor play area safe and enclosed', category: 'Outdoor' },
  { id: 'playground_equipment', label: 'Playground equipment in good condition', category: 'Outdoor' },
  { id: 'bathrooms_clean', label: 'Bathrooms clean and stocked', category: 'Sanitation' },
  { id: 'diaper_area', label: 'Diaper changing area sanitized', category: 'Sanitation' },
  { id: 'food_storage', label: 'Food storage proper temperature', category: 'Sanitation' },
  { id: 'cleaning_supplies', label: 'Cleaning supplies locked away', category: 'Sanitation' },
  { id: 'medication_locked', label: 'Medications locked and logged', category: 'Health' },
  { id: 'staff_records', label: 'Staff records accessible', category: 'Documentation' },
  { id: 'child_records', label: 'Child records accessible', category: 'Documentation' },
  { id: 'attendance_current', label: 'Daily attendance current', category: 'Documentation' },
];

const InspectionMode = ({ 
  inspectionMode, 
  readinessScore, 
  alerts = [], 
  onUpdate,
  // Props from dashboard for ratio alignment
  staffOnDuty: dashboardStaffOnDuty,
  childrenByAge: dashboardChildrenByAge,
  isNapTime: dashboardIsNapTime,
  attendanceCount: dashboardAttendanceCount,
  // Props for scroll to binder
  scrollToBinder = false,
  onScrollComplete
}) => {
  // Ref for Inspection Binder card
  const binderCardRef = useRef(null);
  
  const [activateDialogOpen, setActivateDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [inspectorEmail, setInspectorEmail] = useState("");
  const [sendingEmail, setSendingEmail] = useState(false);
  const [sendingIndividualReport, setSendingIndividualReport] = useState(null); // Track which individual report is being sent
  
  // Report selection for email
  const [includeStaffReport, setIncludeStaffReport] = useState(true);
  const [includeChildrenReport, setIncludeChildrenReport] = useState(true);
  const [includeFacilityReport, setIncludeFacilityReport] = useState(true);
  const [includeWalkthroughReport, setIncludeWalkthroughReport] = useState(true);
  const [includeEmergencyCards, setIncludeEmergencyCards] = useState(true);
  
  // Inspection window dates - manual input
  const [inspectionStartDate, setInspectionStartDate] = useState("");
  const [inspectionEndDate, setInspectionEndDate] = useState("");
  
  // Walkthrough checklist state
  const [walkthroughChecks, setWalkthroughChecks] = useState({});
  const [walkthroughDate, setWalkthroughDate] = useState("");
  const [customWalkthroughItems, setCustomWalkthroughItems] = useState([]);
  const [showAddItemDialog, setShowAddItemDialog] = useState(false);
  const [newItemLabel, setNewItemLabel] = useState("");
  const [newItemCategory, setNewItemCategory] = useState("Custom");
  
  // Staff ratio state - use dashboard values when available
  const [localStaffOnDuty, setLocalStaffOnDuty] = useState(dashboardStaffOnDuty || 0);
  const [localIsNapTime, setLocalIsNapTime] = useState(dashboardIsNapTime || false);
  const [ratioVerified, setRatioVerified] = useState(false);
  const [ratioVerifiedDate, setRatioVerifiedDate] = useState("");
  const [staffEnteredManually, setStaffEnteredManually] = useState(false);
  
  // Real compliance data from API
  const [facilityItems, setFacilityItems] = useState([]);
  const [emergencyItems, setEmergencyItems] = useState([]);
  const [loadingComplianceData, setLoadingComplianceData] = useState(true);
  const [itemDocuments, setItemDocuments] = useState({}); // Store documents for each item
  
  // Selected items for individual email sends
  const [selectedFacilityItems, setSelectedFacilityItems] = useState({});
  const [selectedEmergencyItems, setSelectedEmergencyItems] = useState({});
  
  // Email preview state
  const [showPreview, setShowPreview] = useState(false);
  const [previewContent, setPreviewContent] = useState("");
  const [loadingPreview, setLoadingPreview] = useState(false);
  
  // Upload and paper copy state
  const [expandedFacilityItem, setExpandedFacilityItem] = useState(null);
  const [uploadingItem, setUploadingItem] = useState(null);
  const [togglingPaperCopy, setTogglingPaperCopy] = useState(null);
  const fileInputRefs = useRef({});
  
  // Inspection Binder Generator state
  const [centerInfo, setCenterInfo] = useState({
    name: "",
    centerAddress: "",
    centerCity: "",
    centerState: "",
    centerZip: "",
    centerPhone: "",
    mpiNumber: "",
    licenseNumber: "",
    directors: [{ name: "", title: "Director" }]
  });
  const [loadingCenterInfo, setLoadingCenterInfo] = useState(true);
  const [savingCenterInfo, setSavingCenterInfo] = useState(false);
  const [generatingBinder, setGeneratingBinder] = useState(false);
  const [emailingBinderToOwner, setEmailingBinderToOwner] = useState(false);
  const [ownerEmail, setOwnerEmail] = useState("");
  const [showOwnerEmailDialog, setShowOwnerEmailDialog] = useState(false);
  
  // Include cover page in inspector email
  const [includeCoverPage, setIncludeCoverPage] = useState(false);

  // Sync with dashboard values
  useEffect(() => {
    if (dashboardStaffOnDuty !== undefined) setLocalStaffOnDuty(dashboardStaffOnDuty);
    if (dashboardIsNapTime !== undefined) setLocalIsNapTime(dashboardIsNapTime);
  }, [dashboardStaffOnDuty, dashboardIsNapTime]);

  // Scroll to Inspection Binder card when triggered
  useEffect(() => {
    if (scrollToBinder && binderCardRef.current) {
      // Small delay to ensure content is rendered
      const timer = setTimeout(() => {
        binderCardRef.current.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'start' 
        });
        // Callback to reset the scroll trigger
        if (onScrollComplete) {
          onScrollComplete();
        }
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [scrollToBinder, onScrollComplete]);

  // Fetch real compliance data from API
  useEffect(() => {
    const fetchComplianceData = async () => {
      setLoadingComplianceData(true);
      try {
        const response = await axios.get(`${API}/compliance/status`);
        const items = response.data.items || [];
        
        // Process facility documents
        const facility = items
          .filter(item => item.category === 'facility_documents')
          .map(item => {
            const isComplete = item.complianceStatus === 'complete';
            const isExpiring = item.complianceStatus === 'expiring';
            
            // Calculate days until expiration
            let expiringDays = null;
            if (item.expirationDate) {
              const expDate = new Date(item.expirationDate);
              const today = new Date();
              expiringDays = Math.ceil((expDate - today) / (1000 * 60 * 60 * 24));
            }
            
            return {
              id: item.id,
              name: item.name,
              // Matches the Compliance Checklist's "Received" logic — a paper
              // copy or uploaded document also counts as compliant, not just
              // an explicit complianceStatus of complete/expiring.
              complete: isComplete || isExpiring || !!item.hasPaperCopy || !!item.hasUploadedDocument || (item.documentCount || 0) > 0,
              expiring: isExpiring,
              expiringDays: expiringDays,
              hasPaperCopy: item.hasPaperCopy || false,
              hasDocument: item.hasUploadedDocument || false,
              documentCount: item.documentCount || 0
            };
          });
        
        // Process emergency preparedness
        const emergency = items
          .filter(item => item.category === 'emergency_preparedness')
          .map(item => ({
            id: item.id,
            name: item.name,
            complete: item.complianceStatus === 'complete' || item.complianceStatus === 'expiring' || !!item.hasPaperCopy || !!item.hasUploadedDocument || (item.documentCount || 0) > 0,
            hasPaperCopy: item.hasPaperCopy || false,
            hasDocument: item.hasUploadedDocument || false,
            documentCount: item.documentCount || 0
          }));
        
        setFacilityItems(facility);
        setEmergencyItems(emergency);
        
        // Fetch documents for items that have them
        const allItems = [...facility, ...emergency];
        const docsPromises = allItems
          .filter(item => item.hasDocument)
          .map(async (item) => {
            try {
              const docsResponse = await axios.get(`${API}/compliance/documents/${item.id}`);
              return { itemId: item.id, documents: docsResponse.data.documents || [] };
            } catch (e) {
              return { itemId: item.id, documents: [] };
            }
          });
        
        const docsResults = await Promise.all(docsPromises);
        const docsMap = {};
        docsResults.forEach(result => {
          docsMap[result.itemId] = result.documents;
        });
        setItemDocuments(docsMap);
        
      } catch (error) {
        console.error("Failed to fetch compliance data:", error);
        // Fallback to default items if API fails
        setFacilityItems([
          { id: 'annual_excursion', name: 'Annual Excursion Letter', complete: false, hasDocument: false },
          { id: 'fire_drill_log', name: 'Fire Drill & Fire Detection Log', complete: false, hasDocument: false },
          { id: 'liability_insurance', name: 'Liability Insurance', complete: false, hasDocument: false },
        ]);
        setEmergencyItems([
          { id: 'emergency_procedure', name: 'Emergency Plan & Procedure', complete: false, hasDocument: false },
          { id: 'evacuation_routes', name: 'Evacuation Routes Posted', complete: false, hasDocument: false },
        ]);
      } finally {
        setLoadingComplianceData(false);
      }
    };
    
    fetchComplianceData();
  }, []);

  // Fetch center info for binder
  useEffect(() => {
    const fetchCenterInfo = async () => {
      try {
        const response = await axios.get(`${API}/center-info`);
        setCenterInfo({
          name: response.data.name || "",
          centerAddress: response.data.centerAddress || "",
          centerCity: response.data.centerCity || "",
          centerState: response.data.centerState || "",
          centerZip: response.data.centerZip || "",
          centerPhone: response.data.centerPhone || "",
          mpiNumber: response.data.mpiNumber || "",
          licenseNumber: response.data.licenseNumber || "",
          directors: response.data.directors?.length > 0 ? response.data.directors : [{ name: "", title: "Director" }]
        });
      } catch (error) {
        console.error("Failed to fetch center info:", error);
      } finally {
        setLoadingCenterInfo(false);
      }
    };
    fetchCenterInfo();
  }, []);

  // Save center info
  const saveCenterInfo = async () => {
    setSavingCenterInfo(true);
    try {
      await axios.put(`${API}/center-info`, {
        centerAddress: centerInfo.centerAddress,
        centerCity: centerInfo.centerCity,
        centerState: centerInfo.centerState,
        centerZip: centerInfo.centerZip,
        centerPhone: centerInfo.centerPhone,
        mpiNumber: centerInfo.mpiNumber,
        licenseNumber: centerInfo.licenseNumber,
        directors: centerInfo.directors.filter(d => d.name.trim() !== "")
      });
      toast.success("Center information saved");
    } catch (error) {
      console.error("Failed to save center info:", error);
      toast.error("Failed to save center information");
    } finally {
      setSavingCenterInfo(false);
    }
  };

  // Add director
  const addDirector = () => {
    setCenterInfo(prev => ({
      ...prev,
      directors: [...prev.directors, { name: "", title: "" }]
    }));
  };

  // Remove director
  const removeDirector = (index) => {
    setCenterInfo(prev => ({
      ...prev,
      directors: prev.directors.filter((_, i) => i !== index)
    }));
  };

  // Update director
  const updateDirector = (index, field, value) => {
    setCenterInfo(prev => ({
      ...prev,
      directors: prev.directors.map((d, i) => i === index ? { ...d, [field]: value } : d)
    }));
  };

  // Generate inspection binder PDF
  const generateInspectionBinder = async () => {
    setGeneratingBinder(true);
    try {
      // Call backend API to generate PDF
      const response = await axios.post(`${API}/compliance/generate-binder`, {
        includeCoverPage: true,
        includeReadinessScore: true,
        includeStaffReport: true,
        includeChildReport: true,
        includeEmergencyDocs: true,
        includeFacilityDocs: true,
        includeEnrolledList: true,
        includeWalkthrough: true,
        includeNotesPage: true
      }, {
        responseType: 'blob'
      });
      
      // Create download link for the PDF
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const today = new Date().toISOString().split('T')[0].replace(/-/g, '');
      link.setAttribute('download', `Inspection_Binder_${today}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("Inspection Binder PDF downloaded successfully!");
    } catch (error) {
      console.error("Failed to generate binder:", error);
      toast.error(error.response?.data?.detail || "Failed to generate binder PDF");
    } finally {
      setGeneratingBinder(false);
    }
  };

  // Print binder (opens in new window for printing)
  const printInspectionBinder = async () => {
    setGeneratingBinder(true);
    try {
      // Get PDF and open in new window
      const response = await axios.post(`${API}/compliance/generate-binder`, {
        includeCoverPage: true,
        includeReadinessScore: true,
        includeStaffReport: true,
        includeChildReport: true,
        includeEmergencyDocs: true,
        includeFacilityDocs: true,
        includeEnrolledList: true,
        includeWalkthrough: true,
        includeNotesPage: true
      }, {
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const printWindow = window.open(url, '_blank');
      if (printWindow) {
        printWindow.onload = () => {
          printWindow.print();
        };
      }
      toast.success("Binder opened for printing");
    } catch (error) {
      console.error("Failed to print binder:", error);
      toast.error(error.response?.data?.detail || "Failed to generate binder for printing");
    } finally {
      setGeneratingBinder(false);
    }
  };

  // Handle file upload for facility item
  const handleFacilityFileUpload = async (itemId, file) => {
    if (!file) return;
    
    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error("File size must be less than 10MB");
      return;
    }
    
    setUploadingItem(itemId);
    
    try {
      const formData = new FormData();
      formData.append("file", file);
      
      await axios.post(`${API}/compliance/status/${itemId}/upload`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      
      toast.success("Document uploaded successfully");
      
      // Update local state to reflect the change
      setFacilityItems(prev => prev.map(item => 
        item.id === itemId ? { ...item, complete: true, hasUploadedDocument: true } : item
      ));
      
      onUpdate?.();
    } catch (error) {
      console.error("Upload error:", error);
      toast.error(error.response?.data?.detail || "Failed to upload document");
    } finally {
      setUploadingItem(null);
    }
  };

  // Handle paper copy toggle for facility item
  const handlePaperCopyToggle = async (itemId) => {
    setTogglingPaperCopy(itemId);
    
    try {
      const response = await axios.put(`${API}/compliance/status/${itemId}/paper-copy`);
      
      toast.success("Paper copy status updated");
      
      // Update local state to reflect the change
      const newHasPaperCopy = response.data.hasPaperCopy;
      setFacilityItems(prev => prev.map(item => 
        item.id === itemId ? { 
          ...item, 
          hasPaperCopy: newHasPaperCopy,
          complete: newHasPaperCopy || item.hasUploadedDocument
        } : item
      ));
      
      onUpdate?.();
    } catch (error) {
      console.error("Toggle error:", error);
      toast.error("Failed to update paper copy status");
    } finally {
      setTogglingPaperCopy(null);
    }
  };

  const isActive = inspectionMode?.isActive;
  const score = readinessScore?.score || 87;

  // All walkthrough items (default + custom)
  const allWalkthroughItems = [...WALKTHROUGH_CHECKLIST, ...customWalkthroughItems];

  // Calculate completion stats
  const facilityComplete = facilityItems.filter(i => i.complete).length;
  const facilityTotal = facilityItems.length;
  const emergencyComplete = emergencyItems.filter(i => i.complete).length;
  const emergencyTotal = emergencyItems.length;
  
  // Walkthrough completion - includes custom items
  const walkthroughComplete = Object.values(walkthroughChecks).filter(Boolean).length;
  const walkthroughTotal = allWalkthroughItems.length;
  const walkthroughIsCompliant = walkthroughTotal > 0 && walkthroughComplete === walkthroughTotal;

  // Get inspection window dates
  const getInspectionDates = () => {
    // Helper to parse date string as local date (not UTC)
    const parseLocalDate = (dateStr) => {
      const [year, month, day] = dateStr.split('-').map(Number);
      return new Date(year, month - 1, day); // month is 0-indexed
    };
    
    if (inspectionStartDate && inspectionEndDate) {
      const start = parseLocalDate(inspectionStartDate);
      const end = parseLocalDate(inspectionEndDate);
      return {
        start: start.toLocaleDateString('en-US', { month: 'long', day: 'numeric' }),
        end: end.toLocaleDateString('en-US', { month: 'long', day: 'numeric' })
      };
    }
    if (inspectionMode?.expectedInspectionDate) {
      const start = parseLocalDate(inspectionMode.expectedInspectionDate);
      const end = new Date(start);
      end.setDate(end.getDate() + 30);
      return {
        start: start.toLocaleDateString('en-US', { month: 'long', day: 'numeric' }),
        end: end.toLocaleDateString('en-US', { month: 'long', day: 'numeric' })
      };
    }
    return { start: 'Not Set', end: 'Not Set' };
  };

  const inspectionDates = getInspectionDates();

  // Calculate ratio compliance using dashboard values
  // Only shows "Compliant" after director manually enters staff count
  const calculateRatioCompliance = () => {
    const staffCount = parseInt(localStaffOnDuty) || 0;
    const childrenByAge = dashboardChildrenByAge || { infant: 0, youngToddler: 0, olderToddler: 0, preschool: 0, schoolAge: 0 };
    const totalChildren = Object.values(childrenByAge).reduce((sum, count) => sum + (parseInt(count) || 0), 0);
    
    // If staff count not entered or no children, show "Not Verified"
    if (!staffEnteredManually || staffCount <= 0) {
      return { 
        compliant: false, 
        notVerified: true,
        message: 'Enter staff count to verify', 
        staffCount, 
        totalChildren,
        requiredStaff: null
      };
    }
    
    if (totalChildren === 0) {
      return { 
        compliant: false, 
        notVerified: true,
        message: 'No children signed in', 
        staffCount, 
        totalChildren,
        requiredStaff: null
      };
    }
    
    // Find youngest age group for mixed ratio
    const ageOrder = ['infant', 'youngToddler', 'olderToddler', 'preschool', 'schoolAge'];
    let youngestAgeGroup = 'schoolAge';
    
    for (const age of ageOrder) {
      if (parseInt(childrenByAge[age]) > 0) {
        youngestAgeGroup = age;
        break;
      }
    }
    
    const ratioToUse = localIsNapTime ? RATIOS[youngestAgeGroup].napRatio : RATIOS[youngestAgeGroup].ratio;
    const requiredStaff = Math.ceil(totalChildren / ratioToUse);
    const compliant = staffCount >= requiredStaff;
    
    return {
      compliant,
      notVerified: false,
      staffCount,
      totalChildren,
      requiredStaff,
      ratio: `1:${ratioToUse}`,
      ageGroup: RATIOS[youngestAgeGroup].label,
      isNapTime: localIsNapTime
    };
  };

  const ratioStatus = calculateRatioCompliance();

  // Activate inspection mode
  const activateInspectionMode = async () => {
    if (!inspectionStartDate || !inspectionEndDate) {
      toast.error("Please set inspection window dates");
      return;
    }
    
    setSaving(true);
    try {
      await axios.put(`${API}/compliance/inspection-mode`, {
        isActive: true,
        expectedInspectionDate: inspectionStartDate,
        notes: `Inspection window: ${inspectionStartDate} to ${inspectionEndDate}`
      });
      toast.success("Inspection Mode activated");
      setActivateDialogOpen(false);
      onUpdate?.();
      // Scroll to top of page
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error) {
      toast.error("Failed to activate inspection mode");
    } finally {
      setSaving(false);
    }
  };

  // Deactivate inspection mode
  const deactivateInspectionMode = async () => {
    try {
      await axios.put(`${API}/compliance/inspection-mode`, { isActive: false });
      toast.success("Inspection Mode deactivated");
      onUpdate?.();
    } catch (error) {
      toast.error("Failed to deactivate");
    }
  };

  // Toggle walkthrough item
  const toggleWalkthroughItem = (itemId) => {
    setWalkthroughChecks(prev => ({
      ...prev,
      [itemId]: !prev[itemId]
    }));
  };

  // Confirm ratio verification
  const confirmRatioVerification = () => {
    if (ratioStatus.compliant && !ratioStatus.notVerified) {
      setRatioVerified(true);
      setRatioVerifiedDate(new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric' }));
      toast.success("Staff-to-Child Ratios confirmed compliant");
    } else {
      toast.error("Cannot confirm - enter staff count and verify ratios first");
    }
  };

  // Add custom walkthrough item
  const addCustomWalkthroughItem = () => {
    if (!newItemLabel.trim()) {
      toast.error("Please enter an item description");
      return;
    }
    
    const newItem = {
      id: `custom_${Date.now()}`,
      label: newItemLabel.trim(),
      category: newItemCategory || 'Custom',
      isCustom: true
    };
    
    setCustomWalkthroughItems(prev => [...prev, newItem]);
    setNewItemLabel("");
    setNewItemCategory("Custom");
    setShowAddItemDialog(false);
    toast.success("Walkthrough item added");
  };

  // Remove custom walkthrough item
  const removeCustomWalkthroughItem = (itemId) => {
    setCustomWalkthroughItems(prev => prev.filter(item => item.id !== itemId));
    // Also remove from checks
    setWalkthroughChecks(prev => {
      const newChecks = { ...prev };
      delete newChecks[itemId];
      return newChecks;
    });
    toast.success("Item removed");
  };

  // Handle staff count manual entry
  const handleStaffCountChange = (value) => {
    const numValue = parseInt(value) || 0;
    setLocalStaffOnDuty(numValue);
    setStaffEnteredManually(true);
    setRatioVerified(false); // Reset verification when staff count changes
  };

  // View document in new window
  const viewDocument = async (itemId, docId, fileName) => {
    try {
      const response = await axios.get(`${API}/compliance/documents/${itemId}/${docId}/download`, {
        responseType: 'blob'
      });
      const blob = new Blob([response.data], { type: response.headers['content-type'] });
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch (error) {
      console.error("Failed to view document:", error);
      toast.error("Failed to open document");
    }
  };

  // Print individual document
  const printDocument = async (itemId, docId, fileName) => {
    try {
      const response = await axios.get(`${API}/compliance/documents/${itemId}/${docId}/download`, {
        responseType: 'blob'
      });
      const blob = new Blob([response.data], { type: response.headers['content-type'] });
      const url = window.URL.createObjectURL(blob);
      
      // Open in new window and print
      const printWindow = window.open(url, '_blank');
      if (printWindow) {
        printWindow.onload = () => {
          printWindow.print();
        };
      }
    } catch (error) {
      console.error("Failed to print document:", error);
      toast.error("Failed to print document");
    }
  };

  // Print functions
  const printFacilityChecklist = () => {
    const content = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Facility Checklist</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 40px; max-width: 800px; margin: 0 auto; }
          h1 { color: #1a365d; border-bottom: 2px solid #1a365d; padding-bottom: 10px; }
          .item { padding: 10px; border-bottom: 1px solid #ddd; display: flex; align-items: center; gap: 10px; }
          .complete { color: #28a745; }
          .missing { color: #dc3545; font-weight: bold; }
          .warning { color: #ffc107; }
          .legend { margin-top: 20px; padding: 10px; background: #f8f9fa; border-radius: 5px; }
        </style>
      </head>
      <body>
        <h1>Facility Documents Checklist</h1>
        <p><strong>Completion:</strong> ${facilityComplete}/${facilityTotal}</p>
        ${facilityItems.map(item => `
          <div class="item">
            <span class="${item.complete ? 'complete' : 'missing'}">${item.complete ? '✓' : '✗'}</span>
            <span class="${!item.complete ? 'missing' : ''}">${item.name === "Certificate of Compliance" ? "Certificate of Compliance License" : item.name}</span>
            ${item.expiring ? `<span class="warning">(Expiring in ${item.expiringDays} days)</span>` : ''}
          </div>
        `).join('')}
        <div class="legend"><strong>✗</strong> = Missing Document</div>
        <p style="margin-top: 20px; font-size: 12px; color: #666;">Generated: ${new Date().toLocaleString()}</p>
      </body>
      </html>
    `;
    const printWindow = window.open('', '_blank');
    printWindow.document.write(content);
    printWindow.document.close();
    printWindow.print();
  };

  const printPreparednessChecklist = () => {
    const content = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Emergency Preparedness</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 40px; max-width: 800px; margin: 0 auto; }
          h1 { color: #1a365d; border-bottom: 2px solid #1a365d; padding-bottom: 10px; }
          .item { padding: 10px; border-bottom: 1px solid #ddd; display: flex; align-items: center; gap: 10px; }
          .complete { color: #28a745; }
          .missing { color: #dc3545; font-weight: bold; }
          .legend { margin-top: 20px; padding: 10px; background: #f8f9fa; border-radius: 5px; }
        </style>
      </head>
      <body>
        <h1>Emergency Preparedness List</h1>
        <p><strong>Completion:</strong> ${emergencyComplete}/${emergencyTotal}</p>
        ${emergencyItems.map(item => `
          <div class="item">
            <span class="${item.complete ? 'complete' : 'missing'}">${item.complete ? '✓' : '✗'}</span>
            <span class="${!item.complete ? 'missing' : ''}">${item.name}</span>
          </div>
        `).join('')}
        <div class="legend"><strong>✗</strong> = Missing Document</div>
        <p style="margin-top: 20px; font-size: 12px; color: #666;">Generated: ${new Date().toLocaleString()}</p>
      </body>
      </html>
    `;
    const printWindow = window.open('', '_blank');
    printWindow.document.write(content);
    printWindow.document.close();
    printWindow.print();
  };

  const printWalkthroughReport = () => {
    const content = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Facility Walkthrough Report</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 40px; max-width: 800px; margin: 0 auto; }
          h1 { color: #1a365d; border-bottom: 2px solid #1a365d; padding-bottom: 10px; }
          h2 { color: #1a365d; margin-top: 20px; font-size: 14px; }
          .item { padding: 8px 10px; border-bottom: 1px solid #eee; display: flex; align-items: center; gap: 10px; }
          .checked { color: #28a745; }
          .unchecked { color: #dc3545; }
          .summary { background: #f8f9fa; padding: 15px; border-radius: 5px; margin-bottom: 20px; }
        </style>
      </head>
      <body>
        <h1>Facility Walkthrough Report</h1>
        <div class="summary">
          <p><strong>Date:</strong> ${walkthroughDate || new Date().toLocaleDateString()}</p>
          <p><strong>Completion:</strong> ${walkthroughComplete}/${walkthroughTotal} items checked</p>
        </div>
        ${['Safety', 'Outdoor', 'Sanitation', 'Health', 'Documentation'].map(category => `
          <h2>${category}</h2>
          ${WALKTHROUGH_CHECKLIST.filter(i => i.category === category).map(item => `
            <div class="item">
              <span class="${walkthroughChecks[item.id] ? 'checked' : 'unchecked'}">${walkthroughChecks[item.id] ? '✓' : '○'}</span>
              <span>${item.label}</span>
            </div>
          `).join('')}
        `).join('')}
        <p style="margin-top: 30px; font-size: 12px; color: #666;">Generated: ${new Date().toLocaleString()}</p>
      </body>
      </html>
    `;
    const printWindow = window.open('', '_blank');
    printWindow.document.write(content);
    printWindow.document.close();
    printWindow.print();
  };

  // Preview email content before sending
  const previewInspectionReport = async () => {
    if (!inspectorEmail || !inspectorEmail.includes('@')) {
      toast.error("Please enter a valid email address");
      return;
    }
    
    if (!includeStaffReport && !includeChildrenReport && !includeFacilityReport && !includeWalkthroughReport && !includeEmergencyCards) {
      toast.error("Please select at least one report to preview");
      return;
    }
    
    setLoadingPreview(true);
    try {
      const response = await axios.post(`${API}/compliance/preview-inspection-report`, {
        inspectorEmail,
        inspectionStartDate: inspectionStartDate,
        inspectionEndDate: inspectionEndDate,
        walkthroughChecks,
        staffOnDuty: localStaffOnDuty,
        isNapTime: localIsNapTime,
        includeStaffReport,
        includeChildrenReport,
        includeFacilityReport,
        includeWalkthroughReport,
        includeEmergencyCards,
        walkthroughIsCompliant
      });
      
      if (response.data.html) {
        setPreviewContent(response.data.html);
        setShowPreview(true);
      } else {
        toast.error("Failed to generate preview");
      }
    } catch (error) {
      console.error("Failed to preview inspection report:", error);
      toast.error("Failed to generate preview");
    } finally {
      setLoadingPreview(false);
    }
  };

  // Email reports - Send real inspection report via API
  const emailReportsToInspector = async () => {
    if (!inspectorEmail || !inspectorEmail.includes('@')) {
      toast.error("Please enter a valid email address");
      return;
    }
    
    // Check if at least one report is selected
    if (!includeStaffReport && !includeChildrenReport && !includeFacilityReport && !includeWalkthroughReport && !includeEmergencyCards) {
      toast.error("Please select at least one report to send");
      return;
    }
    
    setSendingEmail(true);
    try {
      const response = await axios.post(`${API}/compliance/send-inspection-report`, {
        inspectorEmail,
        inspectionStartDate: inspectionStartDate,
        inspectionEndDate: inspectionEndDate,
        walkthroughChecks,
        staffOnDuty: localStaffOnDuty,
        isNapTime: localIsNapTime,
        includeStaffReport,
        includeChildrenReport,
        includeFacilityReport,
        includeWalkthroughReport,
        includeEmergencyCards,
        walkthroughIsCompliant
      });
      
      if (response.data.success) {
        const count = response.data.attachmentCount || 0;
        toast.success(`${count} document(s) sent as attachments to ${inspectorEmail}!`);
      } else {
        toast.error("Failed to send email");
      }
    } catch (error) {
      console.error("Failed to send inspection report:", error);
      const errorMessage = error.response?.data?.detail || "Failed to send email. Please try again.";
      toast.error(errorMessage);
    } finally {
      setSendingEmail(false);
    }
  };

  // Send individual report to inspector (for updates/resends)
  const sendIndividualReport = async (reportType, selectedItems = null) => {
    if (!inspectorEmail || !inspectorEmail.includes('@')) {
      toast.error("Please enter inspector email at the bottom of the page first");
      return;
    }
    
    // Check if items are selected when sending specific items
    if (selectedItems) {
      const selectedCount = Object.values(selectedItems).filter(Boolean).length;
      if (selectedCount === 0) {
        toast.error("Please select at least one item to email");
        return;
      }
    }
    
    setSendingIndividualReport(reportType);
    try {
      const response = await axios.post(`${API}/compliance/send-inspection-report`, {
        inspectorEmail,
        inspectionStartDate: inspectionStartDate,
        inspectionEndDate: inspectionEndDate,
        walkthroughChecks,
        staffOnDuty: localStaffOnDuty,
        isNapTime: localIsNapTime,
        // Only include the selected report type
        includeStaffReport: reportType === 'staff',
        includeChildrenReport: reportType === 'children',
        includeFacilityReport: reportType === 'facility',
        includeWalkthroughReport: false,
        includeEmergencyCards: reportType === 'emergency',
        walkthroughIsCompliant,
        isIndividualReport: true,
        reportType: reportType,
        // Send selected items for granular control
        selectedItems: selectedItems
      });
      
      const reportNames = {
        facility: 'Facility Documents',
        staff: 'Staff Records',
        children: 'Children Records',
        emergency: 'Emergency Cards'
      };
      
      const selectedCount = selectedItems ? Object.values(selectedItems).filter(Boolean).length : 'all';
      
      if (response.data.success) {
        toast.success(`${selectedCount} ${reportNames[reportType]} item(s) sent to ${inspectorEmail}!`);
      } else {
        toast.error("Failed to send email");
      }
    } catch (error) {
      console.error("Failed to send individual report:", error);
      const errorMessage = error.response?.data?.detail || "Failed to send email. Please try again.";
      toast.error(errorMessage);
    } finally {
      setSendingIndividualReport(null);
    }
  };

  // Toggle all items in a category
  const toggleAllFacilityItems = (selectAll) => {
    const newSelected = {};
    facilityItems.forEach(item => {
      newSelected[item.id] = selectAll;
    });
    setSelectedFacilityItems(newSelected);
  };

  const toggleAllEmergencyItems = (selectAll) => {
    const newSelected = {};
    emergencyItems.forEach(item => {
      newSelected[item.id] = selectAll;
    });
    setSelectedEmergencyItems(newSelected);
  };

  // Helper function to get documents list for selected items (for hover preview)
  const getSelectedDocumentsList = (items, selectedItems, documents) => {
    const selectedIds = Object.entries(selectedItems)
      .filter(([_, selected]) => selected)
      .map(([id]) => id);
    
    const docsList = [];
    selectedIds.forEach(itemId => {
      const item = items.find(i => i.id === itemId);
      const itemDocs = documents[itemId] || [];
      if (item && itemDocs.length > 0) {
        itemDocs.forEach(doc => {
          docsList.push({
            itemName: item.name,
            fileName: doc.fileName
          });
        });
      } else if (item && item.hasDocument) {
        docsList.push({
          itemName: item.name,
          fileName: "(document available)"
        });
      }
    });
    return docsList;
  };

  // If not active, show activation prompt
  if (!isActive) {
    return (
      <div className="space-y-6">
        <Card className="border-2 border-dashed border-gray-300 bg-gray-50">
          <CardContent className="py-12 text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#1a365d] flex items-center justify-center">
              <Shield className="w-8 h-8 text-white" />
            </div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">Enter Inspection Mode</h2>
            <p className="text-gray-600 max-w-md mx-auto mb-6 text-sm">
              Activate when you are within your inspection window. Set your expected inspection dates to enable preparation tracking.
            </p>
            <Button
              onClick={() => setActivateDialogOpen(true)}
              size="lg"
              className="bg-[#1a365d] hover:bg-[#2d4a7c] text-white gap-2"
            >
              <Calendar className="w-5 h-5" />
              Set Inspection Window
            </Button>
          </CardContent>
        </Card>

        {/* Activation Dialog with Date Input */}
        <Dialog open={activateDialogOpen} onOpenChange={setActivateDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Calendar className="w-5 h-5 text-[#1a365d]" />
                Set Inspection Window
              </DialogTitle>
              <DialogDescription>
                Enter your expected inspection window dates
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="start-date" className="text-sm font-medium">Start Date</Label>
                  <Input
                    id="start-date"
                    type="date"
                    value={inspectionStartDate}
                    onChange={(e) => setInspectionStartDate(e.target.value)}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="end-date" className="text-sm font-medium">End Date</Label>
                  <Input
                    id="end-date"
                    type="date"
                    value={inspectionEndDate}
                    onChange={(e) => setInspectionEndDate(e.target.value)}
                    className="mt-1"
                  />
                </div>
              </div>
              <p className="text-xs text-gray-500">
                Unannounced inspection may occur within this window.
              </p>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setActivateDialogOpen(false)}>Cancel</Button>
              <Button onClick={activateInspectionMode} disabled={saving} className="bg-[#1a365d] hover:bg-[#2d4a7c]">
                {saving ? "Activating..." : "Activate Inspection Mode"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // Active Inspection Mode UI
  return (
    <div className="space-y-4">
      {/* Inspection Window Banner with Date Inputs */}
      <div className="bg-[#ffc107] rounded-lg p-4" data-testid="inspection-active-banner">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
              <AlertTriangle className="w-6 h-6 text-[#1a365d]" />
            </div>
            <div>
              <p className="font-bold text-[#1a365d] text-lg">Inspection Window Active</p>
              <p className="text-[#1a365d]/80 text-sm">{inspectionDates.start} – {inspectionDates.end}</p>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={deactivateInspectionMode} className="text-[#1a365d] hover:bg-white/20">
            Exit Mode
          </Button>
        </div>
        
        {/* Manual Date Adjustment */}
        <div className="bg-white/20 rounded p-2 mt-2">
          <p className="text-xs font-medium text-[#1a365d] mb-2">Adjust Inspection Window:</p>
          <div className="flex gap-2 items-center">
            <Input
              type="date"
              value={inspectionStartDate}
              onChange={(e) => setInspectionStartDate(e.target.value)}
              className="h-8 text-xs bg-white"
            />
            <span className="text-[#1a365d]">to</span>
            <Input
              type="date"
              value={inspectionEndDate}
              onChange={(e) => setInspectionEndDate(e.target.value)}
              className="h-8 text-xs bg-white"
            />
          </div>
        </div>
      </div>

      {/* Readiness Score */}
      <div className="bg-[#28a745] rounded-lg p-5 text-center">
        <p className="text-white text-2xl font-bold">Inspection Readiness: {score}%</p>
        <p className="text-white/80 text-sm mt-1">Prepare for licensing inspection</p>
      </div>

      {/* 3 Column Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Facility Documents Card - Shows compliance status only */}
        <Card className="bg-white border shadow-sm">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base font-semibold flex items-center gap-2">
                {facilityComplete === facilityTotal ? (
                  <CheckCircle2 className="w-5 h-5 text-[#28a745]" />
                ) : (
                  <Building className="w-5 h-5 text-[#1a365d]" />
                )}
                Facility Documents
              </CardTitle>
              {facilityComplete < facilityTotal && (
                <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200 text-xs">
                  {facilityTotal - facilityComplete} Missing
                </Badge>
              )}
            </div>
            <p className="text-sm text-gray-500">{facilityComplete} / {facilityTotal} In Compliance</p>
          </CardHeader>
          <CardContent className="space-y-2">
            <Progress 
              value={(facilityComplete / facilityTotal) * 100} 
              className={`h-2 ${facilityComplete === facilityTotal ? '' : '[&>div]:bg-yellow-500'}`}
            />
            
            {/* Select All / Deselect All */}
            <div className="flex items-center justify-between text-xs pt-2">
              <span className="text-gray-500">Select items to email:</span>
              <div className="flex gap-2">
                <button 
                  onClick={() => toggleAllFacilityItems(true)}
                  className="text-[#1a365d] hover:underline"
                >
                  Select All
                </button>
                <span className="text-gray-300">|</span>
                <button 
                  onClick={() => toggleAllFacilityItems(false)}
                  className="text-gray-500 hover:underline"
                >
                  Clear
                </button>
              </div>
            </div>
            
            {/* Items list with checkboxes */}
            <div className="max-h-80 overflow-y-auto space-y-1 mt-2 border rounded p-2">
              {facilityItems.map(item => (
                <div key={item.id} className="border-b last:border-0 py-2">
                  <div className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      <Checkbox
                        id={`facility-${item.id}`}
                        checked={selectedFacilityItems[item.id] || false}
                        onCheckedChange={(checked) => setSelectedFacilityItems(prev => ({...prev, [item.id]: checked}))}
                        className="h-4 w-4"
                      />
                      {item.complete ? (
                        <CheckCircle2 className="w-4 h-4 text-[#28a745] flex-shrink-0" />
                      ) : (
                        <XCircle className="w-4 h-4 text-[#dc3545] flex-shrink-0" />
                      )}
                      <label 
                        htmlFor={`facility-${item.id}`}
                        className={`truncate cursor-pointer ${item.complete ? 'text-gray-700' : 'text-[#dc3545] font-medium'}`}
                      >
                        {item.name}
                      </label>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      {item.hasPaperCopy && (
                        <Paperclip className="w-3 h-3 text-blue-500" title="Paper copy on file" />
                      )}
                      {item.expiring && (
                        <span className="text-[10px] text-[#ffc107] bg-[#ffc107]/10 px-1 py-0.5 rounded">
                          {item.expiringDays}d
                        </span>
                      )}
                      {item.complete ? (
                        <Badge variant="outline" className="bg-green-50 text-green-700 text-[10px] px-1.5">
                          Compliant
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="bg-red-50 text-red-700 text-[10px] px-1.5">
                          Not Compliant
                        </Badge>
                      )}
                    </div>
                  </div>
                  {/* Document View/Print Buttons */}
                  {item.hasDocument && itemDocuments[item.id] && itemDocuments[item.id].length > 0 ? (
                    <div className="ml-6 mt-1 flex flex-wrap gap-1">
                      {itemDocuments[item.id].map(doc => (
                        <div key={doc.id} className="flex items-center gap-1">
                          <button
                            onClick={() => viewDocument(item.id, doc.id, doc.fileName)}
                            className="text-[10px] text-blue-600 hover:text-blue-800 flex items-center gap-0.5 px-1.5 py-0.5 bg-blue-50 rounded hover:bg-blue-100"
                            title={`View ${doc.fileName}`}
                          >
                            <Eye className="w-3 h-3" />
                            View
                          </button>
                          <button
                            onClick={() => printDocument(item.id, doc.id, doc.fileName)}
                            className="text-[10px] text-gray-600 hover:text-gray-800 flex items-center gap-0.5 px-1.5 py-0.5 bg-gray-100 rounded hover:bg-gray-200"
                            title={`Print ${doc.fileName}`}
                          >
                            <Printer className="w-3 h-3" />
                            Print
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : !item.complete && (
                    <div className="ml-6 mt-1">
                      <span className="text-[10px] text-gray-400 italic">No document uploaded</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
            
            <p className="text-[10px] text-gray-400 pt-2 border-t">
              Status pulled from Compliance Checklist. Update documents in Compliance Dashboard.
            </p>

            <div className="flex gap-2 mt-2">
              <Button onClick={printFacilityChecklist} className="flex-1 bg-[#1a365d] hover:bg-[#2d4a7c] text-white">
                <Printer className="w-4 h-4 mr-2" />
                Print
              </Button>
              <TooltipProvider delayDuration={300}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button 
                      onClick={() => sendIndividualReport('facility', selectedFacilityItems)} 
                      disabled={sendingIndividualReport === 'facility' || !inspectorEmail || Object.values(selectedFacilityItems).filter(Boolean).length === 0}
                      variant="outline"
                      className="flex-1 border-[#1a365d] text-[#1a365d] hover:bg-[#1a365d]/10"
                    >
                      <Mail className="w-4 h-4 mr-2" />
                      {sendingIndividualReport === 'facility' ? "Sending..." : `Email (${Object.values(selectedFacilityItems).filter(Boolean).length})`}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="max-w-xs bg-white border shadow-lg p-3">
                    {!inspectorEmail ? (
                      <p className="text-gray-600 text-xs">Enter inspector email below first</p>
                    ) : Object.values(selectedFacilityItems).filter(Boolean).length === 0 ? (
                      <p className="text-gray-600 text-xs">Select items to email</p>
                    ) : (
                      <div className="text-xs">
                        <p className="font-semibold text-gray-800 mb-2">📎 Files to attach:</p>
                        <ul className="space-y-1 max-h-40 overflow-y-auto">
                          {getSelectedDocumentsList(facilityItems, selectedFacilityItems, itemDocuments).map((doc, i) => (
                            <li key={i} className="text-gray-600">
                              <span className="font-medium text-gray-800">{doc.fileName}</span>
                              <br /><span className="text-[10px] text-gray-400">{doc.itemName}</span>
                            </li>
                          ))}
                          {getSelectedDocumentsList(facilityItems, selectedFacilityItems, itemDocuments).length === 0 && (
                            <li className="text-amber-600">No documents uploaded for selected items</li>
                          )}
                        </ul>
                      </div>
                    )}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          </CardContent>
        </Card>

        {/* Staff Records - Using comparison component */}
        <StaffComparisonCard inspectorEmail={inspectorEmail} />

        {/* Children Records - Using comparison component */}
        <ChildrenComparisonCard inspectorEmail={inspectorEmail} />
      </div>

      {/* Emergency Preparedness and Inspection Walkthrough */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Emergency Preparedness - Shows compliance status only */}
        <Card className="bg-white border shadow-sm">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base font-semibold flex items-center gap-2">
                {emergencyComplete === emergencyTotal ? (
                  <CheckCircle2 className="w-5 h-5 text-[#28a745]" />
                ) : (
                  <AlertCircle className="w-5 h-5 text-[#dc3545]" />
                )}
                Emergency Preparedness
              </CardTitle>
              {emergencyComplete < emergencyTotal && (
                <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200 text-xs">
                  {emergencyTotal - emergencyComplete} Missing
                </Badge>
              )}
            </div>
            <p className="text-sm text-gray-500">{emergencyComplete} / {emergencyTotal} In Compliance</p>
          </CardHeader>
          <CardContent className="space-y-2">
            <Progress 
              value={(emergencyComplete / emergencyTotal) * 100} 
              className={`h-2 ${emergencyComplete === emergencyTotal ? '' : '[&>div]:bg-yellow-500'}`}
            />
            
            {/* Select All / Deselect All */}
            <div className="flex items-center justify-between text-xs pt-2">
              <span className="text-gray-500">Select items to email:</span>
              <div className="flex gap-2">
                <button 
                  onClick={() => toggleAllEmergencyItems(true)}
                  className="text-[#1a365d] hover:underline"
                >
                  Select All
                </button>
                <span className="text-gray-300">|</span>
                <button 
                  onClick={() => toggleAllEmergencyItems(false)}
                  className="text-gray-500 hover:underline"
                >
                  Clear
                </button>
              </div>
            </div>
            
            {/* Items list with checkboxes */}
            <div className="max-h-80 overflow-y-auto space-y-1 mt-2 border rounded p-2">
              {emergencyItems.map(item => (
                <div key={item.id} className="border-b last:border-0 py-2">
                  <div className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      <Checkbox
                        id={`emergency-${item.id}`}
                        checked={selectedEmergencyItems[item.id] || false}
                        onCheckedChange={(checked) => setSelectedEmergencyItems(prev => ({...prev, [item.id]: checked}))}
                        className="h-4 w-4"
                      />
                      {item.complete ? (
                        <CheckCircle2 className="w-4 h-4 text-[#28a745] flex-shrink-0" />
                      ) : (
                        <XCircle className="w-4 h-4 text-[#dc3545] flex-shrink-0" />
                      )}
                      <label 
                        htmlFor={`emergency-${item.id}`}
                        className={`truncate cursor-pointer ${item.complete ? 'text-gray-700' : 'text-[#dc3545] font-medium'}`}
                      >
                        {item.name}
                      </label>
                    </div>
                    {item.complete ? (
                      <Badge variant="outline" className="bg-green-50 text-green-700 text-[10px] px-1.5">
                        Compliant
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="bg-red-50 text-red-700 text-[10px] px-1.5">
                        Not Compliant
                      </Badge>
                    )}
                  </div>
                  {/* Document View/Print Buttons */}
                  {item.hasDocument && itemDocuments[item.id] && itemDocuments[item.id].length > 0 ? (
                    <div className="ml-6 mt-1 flex flex-wrap gap-1">
                      {itemDocuments[item.id].map(doc => (
                        <div key={doc.id} className="flex items-center gap-1">
                          <button
                            onClick={() => viewDocument(item.id, doc.id, doc.fileName)}
                            className="text-[10px] text-blue-600 hover:text-blue-800 flex items-center gap-0.5 px-1.5 py-0.5 bg-blue-50 rounded hover:bg-blue-100"
                            title={`View ${doc.fileName}`}
                          >
                            <Eye className="w-3 h-3" />
                            View
                          </button>
                          <button
                            onClick={() => printDocument(item.id, doc.id, doc.fileName)}
                            className="text-[10px] text-gray-600 hover:text-gray-800 flex items-center gap-0.5 px-1.5 py-0.5 bg-gray-100 rounded hover:bg-gray-200"
                            title={`Print ${doc.fileName}`}
                          >
                            <Printer className="w-3 h-3" />
                            Print
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : !item.complete && (
                    <div className="ml-6 mt-1">
                      <span className="text-[10px] text-gray-400 italic">No document uploaded</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
            
            <p className="text-[10px] text-gray-400 pt-2 border-t">
              Status pulled from Compliance Checklist. Update documents in Compliance Dashboard.
            </p>

            <div className="flex gap-2 mt-2">
              <Button onClick={printPreparednessChecklist} className="flex-1 bg-[#1a365d] hover:bg-[#2d4a7c] text-white">
                <Printer className="w-4 h-4 mr-2" />
                Print
              </Button>
              <TooltipProvider delayDuration={300}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button 
                      onClick={() => sendIndividualReport('emergency', selectedEmergencyItems)} 
                      disabled={sendingIndividualReport === 'emergency' || !inspectorEmail || Object.values(selectedEmergencyItems).filter(Boolean).length === 0}
                      variant="outline"
                      className="flex-1 border-[#1a365d] text-[#1a365d] hover:bg-[#1a365d]/10"
                    >
                      <Mail className="w-4 h-4 mr-2" />
                      {sendingIndividualReport === 'emergency' ? "Sending..." : `Email (${Object.values(selectedEmergencyItems).filter(Boolean).length})`}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="max-w-xs bg-white border shadow-lg p-3">
                    {!inspectorEmail ? (
                      <p className="text-gray-600 text-xs">Enter inspector email below first</p>
                    ) : Object.values(selectedEmergencyItems).filter(Boolean).length === 0 ? (
                      <p className="text-gray-600 text-xs">Select items to email</p>
                    ) : (
                      <div className="text-xs">
                        <p className="font-semibold text-gray-800 mb-2">📎 Files to attach:</p>
                        <ul className="space-y-1 max-h-40 overflow-y-auto">
                          {getSelectedDocumentsList(emergencyItems, selectedEmergencyItems, itemDocuments).map((doc, i) => (
                            <li key={i} className="text-gray-600">
                              <span className="font-medium text-gray-800">{doc.fileName}</span>
                              <br /><span className="text-[10px] text-gray-400">{doc.itemName}</span>
                            </li>
                          ))}
                          {getSelectedDocumentsList(emergencyItems, selectedEmergencyItems, itemDocuments).length === 0 && (
                            <li className="text-amber-600">No documents uploaded for selected items</li>
                          )}
                        </ul>
                      </div>
                    )}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          </CardContent>
        </Card>

        {/* Inspection Walkthrough with Checklist */}
        <Card className="bg-white border shadow-sm">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base font-semibold flex items-center gap-2">
                {walkthroughIsCompliant ? (
                  <CheckCircle2 className="w-5 h-5 text-[#28a745]" />
                ) : (
                  <AlertCircle className="w-5 h-5 text-[#ffc107]" />
                )}
                Inspection Walkthrough
              </CardTitle>
              <Badge className={walkthroughIsCompliant ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-600"}>
                {walkthroughComplete}/{walkthroughTotal}
              </Badge>
            </div>
            <p className={`text-sm ${walkthroughIsCompliant ? 'text-[#28a745] font-medium' : 'text-gray-500'}`}>
              {walkthroughIsCompliant ? '✓ Compliant - All Items Checked' : 'Pending - Check All Items'}
            </p>
          </CardHeader>
          <CardContent className="space-y-2">
            <Progress 
              value={(walkthroughComplete / walkthroughTotal) * 100} 
              className={`h-2 ${walkthroughIsCompliant ? '' : '[&>div]:bg-yellow-500'}`}
            />
            
            {/* Walkthrough Date Input */}
            <div className="flex items-center gap-2 mt-2">
              <Label className="text-xs text-gray-500">Walkthrough Date:</Label>
              <Input
                type="date"
                value={walkthroughDate}
                onChange={(e) => setWalkthroughDate(e.target.value)}
                className="h-7 text-xs flex-1"
              />
            </div>
            
            {/* Checklist Items (scrollable) - Shows ALL items including custom */}
            <div className="max-h-48 overflow-y-auto space-y-1 border rounded p-2 bg-gray-50">
              {allWalkthroughItems.map(item => (
                <div key={item.id} className="flex items-center gap-2 py-1 group">
                  <Checkbox
                    id={item.id}
                    checked={walkthroughChecks[item.id] || false}
                    onCheckedChange={() => toggleWalkthroughItem(item.id)}
                    className="h-4 w-4"
                  />
                  <Label htmlFor={item.id} className="text-xs text-gray-700 cursor-pointer flex-1">
                    {item.label}
                    {item.isCustom && <span className="text-[10px] text-blue-500 ml-1">(Custom)</span>}
                  </Label>
                  {item.isCustom && (
                    <button 
                      onClick={() => removeCustomWalkthroughItem(item.id)}
                      className="text-red-400 hover:text-red-600 opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <XCircle className="w-3 h-3" />
                    </button>
                  )}
                </div>
              ))}
            </div>
            
            {/* Add Custom Item Button */}
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => setShowAddItemDialog(true)}
              className="w-full text-xs"
            >
              + Add Custom Walkthrough Item
            </Button>
            
            {/* Add Item Dialog */}
            <Dialog open={showAddItemDialog} onOpenChange={setShowAddItemDialog}>
              <DialogContent className="sm:max-w-md">
                <DialogHeader>
                  <DialogTitle>Add Custom Walkthrough Item</DialogTitle>
                  <DialogDescription>
                    Add a custom item to your facility walkthrough checklist.
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label>Item Description</Label>
                    <Input
                      value={newItemLabel}
                      onChange={(e) => setNewItemLabel(e.target.value)}
                      placeholder="e.g., Storage area organized"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Category</Label>
                    <Input
                      value={newItemCategory}
                      onChange={(e) => setNewItemCategory(e.target.value)}
                      placeholder="e.g., Safety, Sanitation, Custom"
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setShowAddItemDialog(false)}>Cancel</Button>
                  <Button onClick={addCustomWalkthroughItem}>Add Item</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
            
            {/* Staff to Child Ratios Section */}
            <div className="border-t pt-3 mt-2">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  {ratioStatus.notVerified ? (
                    <AlertCircle className="w-4 h-4 text-[#ffc107]" />
                  ) : ratioStatus.compliant ? (
                    <CheckCircle2 className="w-4 h-4 text-[#28a745]" />
                  ) : (
                    <XCircle className="w-4 h-4 text-[#dc3545]" />
                  )}
                  <span className="text-sm font-medium">Staff to Child Ratios</span>
                </div>
                {ratioVerified && (
                  <div className="flex items-center gap-1">
                    <span className="text-xs text-gray-500">{ratioVerifiedDate}</span>
                    <Badge className="bg-[#28a745] text-white text-[10px]">Confirmed</Badge>
                  </div>
                )}
              </div>
              
              {/* Ratio Display - With Manual Staff Entry */}
              <div className="bg-gray-50 rounded p-2 text-xs space-y-2">
                {/* Staff On Duty - Manual Entry */}
                <div className="flex items-center justify-between gap-2">
                  <span className="text-gray-600">Staff On Duty:</span>
                  <Input
                    type="number"
                    min="0"
                    value={localStaffOnDuty || ''}
                    onChange={(e) => handleStaffCountChange(e.target.value)}
                    placeholder="Enter count"
                    className="h-7 w-24 text-xs text-right"
                  />
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Children Signed In:</span>
                  <span className="font-medium">{ratioStatus.totalChildren}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Required Staff:</span>
                  <span className="font-medium">{ratioStatus.requiredStaff || '-'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Status:</span>
                  <span className={`font-medium ${ratioStatus.notVerified ? 'text-[#ffc107]' : ratioStatus.compliant ? 'text-[#28a745]' : 'text-[#dc3545]'}`}>
                    {ratioStatus.notVerified ? 'Not Verified' : ratioStatus.compliant ? 'Compliant' : 'Non-Compliant'}
                  </span>
                </div>
                
                {/* Nap Time Toggle */}
                <div className="flex items-center justify-between pt-1 border-t">
                  <div className="flex items-center gap-1 text-gray-600">
                    <Moon className="w-3 h-3" />
                    <span>Nap Time (2x ratios)</span>
                  </div>
                  <Switch
                    checked={localIsNapTime}
                    onCheckedChange={(checked) => {
                      setLocalIsNapTime(checked);
                      setRatioVerified(false);
                    }}
                    className="scale-75"
                  />
                </div>
              </div>
              
              {!ratioVerified && !ratioStatus.notVerified && ratioStatus.compliant && ratioStatus.totalChildren > 0 && (
                <Button
                  size="sm"
                  onClick={confirmRatioVerification}
                  className="w-full mt-2 bg-[#28a745] hover:bg-[#218838] text-white text-xs"
                >
                  Confirm Ratios Compliant
                </Button>
              )}
              
              {ratioStatus.notVerified && (
                <p className="text-xs text-center text-gray-500 mt-2 italic">
                  Enter staff count above to verify ratio compliance
                </p>
              )}
            </div>

            <Button onClick={printWalkthroughReport} className="w-full mt-2 bg-[#1a365d] hover:bg-[#2d4a7c] text-white">
              <Printer className="w-4 h-4 mr-2" />
              Print Walkthrough Report &gt;
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Inspection Binder Generator Card - Redesigned */}
      <FeatureGate feature="inspection_binder">
        <Card ref={binderCardRef} className="bg-white border shadow-lg mt-6 overflow-hidden" data-testid="inspection-binder-card">
        <CardHeader className="bg-gradient-to-r from-[#1a5d1a] to-[#2e8b2e] text-white pb-4">
          <CardTitle className="text-xl font-bold flex items-center gap-3">
            <FolderOpen className="w-6 h-6" />
            Inspection Binder Generator
          </CardTitle>
          <p className="text-green-100 text-sm mt-1">
            Compile everything you need for licensing inspections in one click.
          </p>
        </CardHeader>
        
        <CardContent className="p-6">
          {/* Main Content Grid - Left: Controls, Right: Preview */}
          <div className="grid lg:grid-cols-2 gap-6">
            
            {/* Left Column - Generate Button & Sections List */}
            <div className="space-y-5">
              {/* Generate Button - Prominent */}
              <Button 
                onClick={generateInspectionBinder}
                disabled={generatingBinder}
                className="w-full h-14 bg-[#1a5d1a] hover:bg-[#2e8b2e] text-white text-lg font-semibold rounded-xl shadow-md transition-all hover:shadow-lg"
                data-testid="generate-binder-btn"
              >
                {generatingBinder ? (
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                ) : (
                  <FolderOpen className="w-5 h-5 mr-2" />
                )}
                {generatingBinder ? "Generating Binder..." : "Generate Inspection Binder"}
              </Button>

              {/* Preview of Binder Sections */}
              <div>
                <p className="text-base font-semibold text-gray-800 mb-3">Preview of Binder Sections:</p>
                <div className="space-y-2">
                  {[
                    "Certificate of Compliance",
                    "Staff Compliance Summary Report",
                    "Child Records Summary Report",
                    "Emergency Preparedness Documents",
                    "Facility Documentation",
                    "List of Enrolled Children",
                    "Facility Walkthrough Checklist",
                    "Licensing Inspection Notes"
                  ].map((section, idx) => (
                    <div key={idx} className="flex items-center gap-3 py-1">
                      <div className="w-5 h-5 rounded bg-green-100 border border-green-300 flex items-center justify-center flex-shrink-0">
                        <CheckCircle2 className="w-4 h-4 text-green-600" />
                      </div>
                      <span className="text-sm text-gray-700">{section}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Right Column - Sample Cover Page Preview */}
            <div>
              <p className="text-sm font-medium text-gray-500 mb-3">Sample Preview</p>
              <div className="bg-white border-2 border-gray-200 rounded-xl shadow-inner p-6">
                {/* Cover Page Preview */}
                <div className="text-center space-y-4">
                  {/* Logo */}
                  <div className="flex justify-center">
                    <img 
                      src="/simple-sign-in-logo.png" 
                      alt="ToDays Kidz Logo" 
                      className="h-14 object-contain"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  </div>
                  <div className="text-center">
                    <h3 className="text-2xl font-bold">
                      <span className="text-[#2F6DB5]">To</span>
                      <span className="text-[#F4A623]">Days</span>
                      <span className="text-[#E83E8C]"> Kidz</span>
                    </h3>
                    <p className="text-gray-500 text-sm">Simple Sign-In</p>
                  </div>

                  {/* Title */}
                  <h4 className="text-lg font-bold text-gray-800 pt-2 border-t">
                    Childcare Inspection Readiness Binder
                  </h4>

                  {/* Center Info Preview */}
                  <div className="text-left text-sm space-y-1 text-gray-700 py-3">
                    <p className="font-semibold text-gray-900">
                      {centerInfo.name || "Your Center Name"}
                    </p>
                    <p>{centerInfo.centerAddress || "1234 Oak Street"}</p>
                    <p>
                      {centerInfo.centerCity || "Anytown"}, {centerInfo.centerState || "PA"} {centerInfo.centerZip || "12345"}
                    </p>
                    <p className="pt-1">
                      <span className="text-gray-500">Phone:</span> {centerInfo.centerPhone || "(555) 123-4567"}
                    </p>
                    <p>
                      <span className="text-gray-500">Director:</span> {centerInfo.directors?.[0]?.name || "Sarah Johnson"}
                    </p>
                  </div>

                  {/* Generated Date */}
                  <p className="text-sm font-semibold text-gray-800">
                    Generated: {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                  </p>

                  {/* Readiness Score Bar */}
                  <div className="bg-[#1a5d1a] text-white rounded-lg py-3 px-4 mt-3">
                    <p className="font-bold text-base">
                      Inspection Readiness Score: <span className="text-yellow-300">{readinessScore?.score || 0}% Ready</span>
                    </p>
                  </div>

                  {/* Status Items - Real-time Data */}
                  <div className="text-left text-sm space-y-1 pt-2">
                    {/* Staff Status */}
                    {readinessScore?.staffStats ? (
                      readinessScore.staffStats.complete === readinessScore.staffStats.total ? (
                        <div className="flex items-center gap-2 text-green-700">
                          <CheckCircle2 className="w-4 h-4" />
                          <span>Staff: {readinessScore.staffStats.complete}/{readinessScore.staffStats.total} Complete</span>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2 text-amber-600">
                          <AlertTriangle className="w-4 h-4" />
                          <span>Staff: {readinessScore.staffStats.missing} need attention</span>
                        </div>
                      )
                    ) : (
                      <div className="flex items-center gap-2 text-gray-500">
                        <CheckCircle2 className="w-4 h-4" />
                        <span>Staff Records: Loading...</span>
                      </div>
                    )}

                    {/* Children Status */}
                    {readinessScore?.childrenStats ? (
                      readinessScore.childrenStats.complete === readinessScore.childrenStats.total ? (
                        <div className="flex items-center gap-2 text-green-700">
                          <CheckCircle2 className="w-4 h-4" />
                          <span>Children: {readinessScore.childrenStats.complete}/{readinessScore.childrenStats.total} Compliant</span>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2 text-amber-600">
                          <AlertTriangle className="w-4 h-4" />
                          <span>Children: {readinessScore.childrenStats.missing} missing documents</span>
                        </div>
                      )
                    ) : (
                      <div className="flex items-center gap-2 text-gray-500">
                        <CheckCircle2 className="w-4 h-4" />
                        <span>Child Records: Loading...</span>
                      </div>
                    )}

                    {/* Emergency Preparedness Status */}
                    {readinessScore?.byCategory?.emergency_preparedness ? (
                      readinessScore.byCategory.emergency_preparedness.missingItems === 0 ? (
                        <div className="flex items-center gap-2 text-green-700">
                          <CheckCircle2 className="w-4 h-4" />
                          <span>Emergency: {readinessScore.byCategory.emergency_preparedness.completedItems}/{readinessScore.byCategory.emergency_preparedness.totalItems} Documents</span>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2 text-amber-600">
                          <AlertTriangle className="w-4 h-4" />
                          <span>Emergency: {readinessScore.byCategory.emergency_preparedness.missingItems} documents needed</span>
                        </div>
                      )
                    ) : (
                      <div className="flex items-center gap-2 text-gray-500">
                        <CheckCircle2 className="w-4 h-4" />
                        <span>Emergency Plans: Loading...</span>
                      </div>
                    )}

                    {/* Facility Documents Status */}
                    {readinessScore?.byCategory?.facility_documents ? (
                      readinessScore.byCategory.facility_documents.missingItems === 0 ? (
                        <div className="flex items-center gap-2 text-green-700">
                          <CheckCircle2 className="w-4 h-4" />
                          <span>Facility: {readinessScore.byCategory.facility_documents.completedItems}/{readinessScore.byCategory.facility_documents.totalItems} Documents</span>
                        </div>
                      ) : readinessScore.byCategory.facility_documents.expiringItems > 0 ? (
                        <div className="flex items-center gap-2 text-amber-600">
                          <AlertTriangle className="w-4 h-4" />
                          <span>Facility: {readinessScore.byCategory.facility_documents.expiringItems} expiring soon</span>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2 text-amber-600">
                          <AlertTriangle className="w-4 h-4" />
                          <span>Facility: {readinessScore.byCategory.facility_documents.missingItems} documents needed</span>
                        </div>
                      )
                    ) : null}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons Row */}
          <div className="flex flex-wrap gap-3 mt-6 pt-6 border-t">
            <Button 
              onClick={generateInspectionBinder}
              disabled={generatingBinder}
              variant="outline"
              className="flex-1 min-w-[150px] h-11 border-[#1a5d1a] text-[#1a5d1a] hover:bg-[#1a5d1a]/10 font-semibold rounded-xl"
              data-testid="download-binder-pdf-btn"
            >
              {generatingBinder ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
              {generatingBinder ? "Generating..." : "Download PDF"}
            </Button>
            <Button 
              onClick={printInspectionBinder}
              disabled={generatingBinder}
              variant="outline"
              className="flex-1 min-w-[150px] h-11 border-gray-400 text-gray-700 hover:bg-gray-100 font-semibold rounded-xl"
              data-testid="print-binder-btn"
            >
              <Printer className="w-4 h-4 mr-2" />
              Print Binder
            </Button>
            <Button 
              variant="outline"
              onClick={() => setShowOwnerEmailDialog(true)}
              className="flex-1 min-w-[150px] h-11 border-gray-400 text-gray-700 hover:bg-gray-100 font-semibold rounded-xl"
              data-testid="email-binder-btn"
            >
              <Mail className="w-4 h-4 mr-2" />
              Email to Inspector
            </Button>
          </div>

          {/* Collapsible Center Information Section */}
          <details className="mt-6 border rounded-xl overflow-hidden">
            <summary className="bg-gray-50 px-4 py-3 cursor-pointer hover:bg-gray-100 transition-colors">
              <span className="font-medium text-gray-800">Edit Center Information</span>
              <span className="text-xs text-gray-500 ml-2">(for cover page)</span>
            </summary>
            <div className="p-4 bg-white">
              <p className="text-xs text-gray-500 mb-4 bg-blue-50 p-2 rounded border border-blue-100">
                This information identifies your center on the cover page. It will also appear when you include the cover page in inspector emails. If left blank, placeholders will be shown.
              </p>

              {loadingCenterInfo ? (
                <div className="flex items-center justify-center py-4">
                  <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
                </div>
              ) : (
                <div className="space-y-3">
                  {/* Center Name */}
                  <div>
                    <Label className="text-xs text-gray-600">Center Name</Label>
                    <Input
                      value={centerInfo.name}
                      onChange={(e) => setCenterInfo(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="Your Childcare Center Name"
                      className="h-9 text-sm"
                    />
                  </div>
                  
                  {/* Address */}
                  <div>
                    <Label className="text-xs text-gray-600">Street Address</Label>
                    <Input
                      value={centerInfo.centerAddress}
                      onChange={(e) => setCenterInfo(prev => ({ ...prev, centerAddress: e.target.value }))}
                      placeholder="123 Main Street"
                      className="h-9 text-sm"
                    />
                  </div>

                  <div className="grid grid-cols-3 gap-2">
                    <div>
                      <Label className="text-xs text-gray-600">City</Label>
                      <Input
                        value={centerInfo.centerCity}
                        onChange={(e) => setCenterInfo(prev => ({ ...prev, centerCity: e.target.value }))}
                        placeholder="City"
                        className="h-9 text-sm"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-gray-600">State</Label>
                      <Input
                        value={centerInfo.centerState}
                        onChange={(e) => setCenterInfo(prev => ({ ...prev, centerState: e.target.value }))}
                        placeholder="PA"
                        className="h-9 text-sm"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-gray-600">ZIP</Label>
                      <Input
                        value={centerInfo.centerZip}
                        onChange={(e) => setCenterInfo(prev => ({ ...prev, centerZip: e.target.value }))}
                        placeholder="19101"
                        className="h-9 text-sm"
                      />
                    </div>
                  </div>

                  <div>
                    <Label className="text-xs text-gray-600">Phone Number</Label>
                    <Input
                      value={centerInfo.centerPhone}
                      onChange={(e) => setCenterInfo(prev => ({ ...prev, centerPhone: e.target.value }))}
                      placeholder="(215) 555-1234"
                      className="h-9 text-sm"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-xs text-gray-600">MPI / Provider ID</Label>
                      <Input
                        value={centerInfo.mpiNumber}
                        onChange={(e) => setCenterInfo(prev => ({ ...prev, mpiNumber: e.target.value }))}
                        placeholder="MPI Number"
                        className="h-9 text-sm"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-gray-600">License Number</Label>
                      <Input
                        value={centerInfo.licenseNumber}
                        onChange={(e) => setCenterInfo(prev => ({ ...prev, licenseNumber: e.target.value }))}
                        placeholder="License #"
                        className="h-9 text-sm"
                      />
                    </div>
                  </div>

                  {/* Directors */}
                  <div>
                    <Label className="text-xs text-gray-600 mb-2 block">Director(s)</Label>
                    <div className="space-y-2">
                      {centerInfo.directors.map((director, index) => (
                        <div key={index} className="flex gap-2 items-center">
                          <Input
                            value={director.name}
                            onChange={(e) => updateDirector(index, 'name', e.target.value)}
                            placeholder="Name"
                            className="h-9 text-sm flex-1"
                          />
                          <Input
                            value={director.title}
                            onChange={(e) => updateDirector(index, 'title', e.target.value)}
                            placeholder="Title"
                            className="h-9 text-sm w-32"
                          />
                          {centerInfo.directors.length > 1 && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => removeDirector(index)}
                              className="h-9 w-9 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      ))}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={addDirector}
                        className="w-full text-xs"
                      >
                        <Plus className="w-3 h-3 mr-1" />
                        Add Another Director
                      </Button>
                    </div>
                  </div>

                  <Button
                    onClick={saveCenterInfo}
                    disabled={savingCenterInfo}
                    className="w-full bg-green-600 hover:bg-green-700 text-white"
                  >
                    {savingCenterInfo ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <CheckCircle2 className="w-4 h-4 mr-2" />
                    )}
                    {savingCenterInfo ? "Saving..." : "Save Center Information"}
                  </Button>
                </div>
              )}
            </div>
          </details>
        </CardContent>
      </Card>
      </FeatureGate>

      {/* Owner Email Dialog */}
      <Dialog open={showOwnerEmailDialog} onOpenChange={setShowOwnerEmailDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Email Binder to Owner</DialogTitle>
            <DialogDescription>
              Send the inspection readiness binder to your center owner for review.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Owner Email Address</Label>
              <Input
                type="email"
                value={ownerEmail}
                onChange={(e) => setOwnerEmail(e.target.value)}
                placeholder="owner@example.com"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowOwnerEmailDialog(false)}>Cancel</Button>
            <Button 
              onClick={() => {
                toast.success(`Binder would be emailed to ${ownerEmail}`);
                setShowOwnerEmailDialog(false);
              }}
              disabled={!ownerEmail || emailingBinderToOwner}
            >
              <Mail className="w-4 h-4 mr-2" />
              Send Binder
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Email Inspection Reports */}
      <LockedOverlay feature="inspection_binder">
        <Card className="bg-white border shadow-sm">
        <CardContent className="py-4 space-y-4">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex items-center gap-4">
              <h3 className="font-semibold text-gray-900">Email Inspection Reports</h3>
              <div className="flex items-center gap-2">
                <Label className="text-sm text-gray-600">Inspector Email:</Label>
                <Input
                  type="email"
                  value={inspectorEmail}
                  onChange={(e) => setInspectorEmail(e.target.value)}
                  className="w-64"
                  placeholder="inspector@example.com"
                />
              </div>
            </div>
          </div>
          
          {/* Report Selection Checkboxes */}
          <div className="border rounded p-3 bg-gray-50">
            <p className="text-sm font-medium text-gray-700 mb-2">Select Reports to Include:</p>
            <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
              <div className="flex items-center gap-2 bg-blue-50 p-2 rounded border border-blue-200">
                <Checkbox
                  id="include-cover"
                  checked={includeCoverPage}
                  onCheckedChange={setIncludeCoverPage}
                />
                <Label htmlFor="include-cover" className="text-sm cursor-pointer font-medium text-blue-700">Cover Page</Label>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox
                  id="include-facility"
                  checked={includeFacilityReport}
                  onCheckedChange={setIncludeFacilityReport}
                />
                <Label htmlFor="include-facility" className="text-sm cursor-pointer">Facility Documents</Label>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox
                  id="include-staff"
                  checked={includeStaffReport}
                  onCheckedChange={setIncludeStaffReport}
                />
                <Label htmlFor="include-staff" className="text-sm cursor-pointer">Staff Records</Label>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox
                  id="include-children"
                  checked={includeChildrenReport}
                  onCheckedChange={setIncludeChildrenReport}
                />
                <Label htmlFor="include-children" className="text-sm cursor-pointer">Children Records</Label>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox
                  id="include-emergency"
                  checked={includeEmergencyCards}
                  onCheckedChange={setIncludeEmergencyCards}
                />
                <Label htmlFor="include-emergency" className="text-sm cursor-pointer">Emergency Preparedness</Label>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox
                  id="include-walkthrough"
                  checked={includeWalkthroughReport}
                  onCheckedChange={setIncludeWalkthroughReport}
                />
                <Label htmlFor="include-walkthrough" className="text-sm cursor-pointer">Walkthrough Summary</Label>
              </div>
            </div>
            {includeCoverPage && (
              <p className="text-xs text-blue-600 mt-2">
                Cover page will include your center information. Edit details in the Inspection Binder Generator section above.
              </p>
            )}
          </div>
          
          <div className="flex gap-2">
            <Button
              onClick={previewInspectionReport}
              disabled={loadingPreview || !inspectorEmail}
              variant="outline"
              className="border-[#1a365d] text-[#1a365d] hover:bg-[#1a365d]/10 gap-2"
            >
              <Eye className="w-4 h-4" />
              {loadingPreview ? "Loading..." : "Preview Email"}
            </Button>
            <TooltipProvider delayDuration={300}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    onClick={emailReportsToInspector}
                    disabled={sendingEmail || !inspectorEmail}
                    className="bg-[#1a365d] hover:bg-[#2d4a7c] text-white gap-2"
                  >
                    <Mail className="w-4 h-4" />
                    {sendingEmail ? "Sending..." : "Send to Inspector"}
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="top" className="max-w-sm bg-white border shadow-lg p-3">
                  <div className="text-xs">
                    <p className="font-semibold text-gray-800 mb-2">Items to send:</p>
                    <ul className="space-y-1">
                      {includeCoverPage && <li className="text-blue-600 font-medium">✓ Cover Page (Center ID)</li>}
                      {includeFacilityReport && <li className="text-gray-600">✓ Facility Documents</li>}
                      {includeStaffReport && <li className="text-gray-600">✓ Staff Records</li>}
                      {includeChildrenReport && <li className="text-gray-600">✓ Children Records</li>}
                      {includeEmergencyCards && <li className="text-gray-600">✓ Emergency Preparedness</li>}
                      {includeWalkthroughReport && <li className="text-gray-600">✓ Walkthrough Summary</li>}
                      {!includeCoverPage && !includeFacilityReport && !includeStaffReport && !includeChildrenReport && !includeEmergencyCards && !includeWalkthroughReport && (
                        <li className="text-amber-600">No items selected</li>
                      )}
                    </ul>
                    <p className="text-[10px] text-gray-400 mt-2 pt-2 border-t">
                      Click "Preview Email" to see exact files that will be attached
                    </p>
                  </div>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        </CardContent>
      </Card>
      </LockedOverlay>

      {/* Email Preview Dialog */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="w-5 h-5 text-[#1a365d]" />
              Email Preview - Document Attachments
            </DialogTitle>
            <DialogDescription>
              These actual documents will be sent as attachments to {inspectorEmail}
            </DialogDescription>
          </DialogHeader>
          <div className="overflow-y-auto max-h-[60vh] border rounded p-4 bg-white">
            <div dangerouslySetInnerHTML={{ __html: previewContent }} />
          </div>
          <DialogFooter className="flex gap-2">
            <Button variant="outline" onClick={() => setShowPreview(false)}>
              Close Preview
            </Button>
            <Button 
              onClick={() => {
                setShowPreview(false);
                emailReportsToInspector();
              }}
              disabled={sendingEmail}
              className="bg-[#1a365d] hover:bg-[#2d4a7c] text-white gap-2"
            >
              <Mail className="w-4 h-4" />
              {sendingEmail ? "Sending..." : "Send with Attachments"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default InspectionMode;
